import type {
  ParameterDefinitionCreateOperation,
  ParameterDimensionDefinitionInOut,
} from '@festina/configuration-general-types/configuration'
import { assertNotEmpty, isEmpty, notEmpty } from '@festina-life/core'
import { RawCheckbox, RawSelect } from '@festina-life/ui/forms'
import { useTranslate, useTranslateEnum } from '@festina-life/ui/hooks'
import { Icon } from '@festina-life/ui/structure'
import cx from 'classnames'
import { type MouseEvent, useId, useState } from 'react'

import { EditorButtonGroup } from '../../../components/editorButtonGroup/EditorButtonGroup'
import { HoverEditButton } from '../../../components/hoverEditGroup/HoverEditButton'
import { useParameterContext } from '../../../context/parameterContext'
import { useOperationsContext } from '../../../workspace/operationsContext'
import { ParameterDimensionTypeViewer } from './ParameterDimensionTypeViewer'
import { ParameterDimensionOptions } from './types'

type Props = {
  data: ParameterDimensionDefinitionInOut
  isCentered: boolean
}

export const ParameterDimensionTypeEditor = ({ isCentered, data }: Props) => {
  const [isEditing, setIsEditing] = useState(false)
  const [dimensionType, setDimensionType] = useState(data.dimensionType)
  const [isRange, setIsRange] = useState(data.isRange)
  const { parameterDefinitionOperation } = useParameterContext()
  const { onAddOrUpdateOperation } = useOperationsContext()
  const translateEnum = useTranslateEnum()
  const translate = useTranslate()
  const isRangeId = useId()

  assertNotEmpty(parameterDefinitionOperation)

  const { internalOperation } = parameterDefinitionOperation

  const matchingDefinitionPosition =
    internalOperation.entity?.dimensionDefinitions?.indexOf(data)

  if (isEmpty(matchingDefinitionPosition) || matchingDefinitionPosition < 0) {
    throw new Error('Unable to match dimension definition position')
  }

  const handleSave = async (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    const updatedOperation = {
      ...parameterDefinitionOperation.internalOperation,
      entity: {
        _type: 'ParameterDefinitionInOut',
        ...parameterDefinitionOperation.internalOperation.entity,
        dimensionDefinitions:
          parameterDefinitionOperation.internalOperation?.entity?.dimensionDefinitions
            ?.filter(notEmpty)
            .map((evaluatedDimensionDefinition) =>
              evaluatedDimensionDefinition !== data
                ? evaluatedDimensionDefinition
                : {
                    ...evaluatedDimensionDefinition,
                    dimensionType,
                    isRange,
                  },
            ),
      },
    } satisfies ParameterDefinitionCreateOperation

    setIsEditing(false)
    await onAddOrUpdateOperation({
      operation: updatedOperation,
      layer: parameterDefinitionOperation.layer,
      block: parameterDefinitionOperation.block,
      // Irrelevant, you can only edit the dimension type of a draft, which can only exist in the workspace.
      shouldOpenUnifiedBranchWorkspace: true,
    })
  }

  const handleCancelEditing = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    setIsRange(data.isRange)
    setDimensionType(data.dimensionType)
    setIsEditing(false)
  }

  return (
    <div
      className={cx('group relative flex size-full items-center', {
        'justify-center': isCentered,
      })}
    >
      {!isEditing ? (
        <div className="flex flex-row items-center gap-x-2">
          <ParameterDimensionTypeViewer data={data} isCentered={isCentered} />
          <div className="invisible flex flex-row gap-x-2 group-hover:visible">
            <HoverEditButton
              icon={<Icon name="Pencil" />}
              onClick={() => setIsEditing(true)}
            />
          </div>
        </div>
      ) : (
        <div className="flex h-full flex-row items-center gap-x-2">
          <RawSelect
            options={ParameterDimensionOptions}
            value={dimensionType}
            onChange={(nextDimensionType) =>
              setDimensionType(nextDimensionType)
            }
            renderFn={(value) =>
              translateEnum('parameter.enum.ParameterDimensionType', value)
            }
            className="max-w-25"
            onClick={(event) => event.stopPropagation()}
          />
          <div className="flex flex-row gap-x-2">
            <label htmlFor={isRangeId} className="text-nowrap">
              {translate('parameterDimensionTypeEditor.label.isRange')}
            </label>
            <RawCheckbox
              id={isRangeId}
              checked={!!isRange}
              onChange={setIsRange}
            />
          </div>
          <EditorButtonGroup
            onCancel={handleCancelEditing}
            onSave={handleSave}
            className="right-2 z-10 mt-2"
          />
        </div>
      )}
    </div>
  )
}
